import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CollectionUserGroupData,
  getInternalQuotesOverviewQueryKeyPrefix,
  InternalQuote,
  InternalSubquote,
  useGetInternalQuotesOverview,
} from '@frontend/api';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Box,
  Button,
  Center,
  ChevronRightIcon,
  Divider,
  Empty,
  EmptyDescription,
  EmptyDrawerIcon,
  EmptyHeadline,
  FormLabel,
  HStack,
  Pagination,
  Part,
  PartLabel,
  PartThumbnail,
  PartThumbnailContainer,
  Spinner,
  Text,
  UserGroupBadge,
  VStack,
} from '@frontend/legacy-components';
import { formatConjunctionWithTerm, useDateFormat } from '@frontend/utils';

import {
  OverscrollShadow,
  OverscrollShadowContainer,
  SHADOW_FROM_DIRECTION,
  useOverflowBox,
} from 'components/OverscrollShadow';
import QuoteStatusBadge from 'components/lib2/QuoteStatusBadge';

import {
  WORKFLOW_OR_QUOTE_ITEM_STATUS,
  WORKFLOW_OR_QUOTE_ITEM_STATUS_LABEL,
} from 'constants/all';

import { statusesToIcon } from 'modules/quotes/quoteItems/components/StatusSelector/CustomWorkflowSteps';
import { QuoteItemStatusBadge } from 'modules/quotes/quoteItems/performanceView/QuoteItemStatusBadge';

import { useUserGroupCollectionContext } from 'utils/multisite/UserGroupCollectionProvider';
import { pluralize } from 'utils/stringUtils';

import {
  Pane,
  PaneBody,
  PaneHeader,
  PaneNoAccessState,
  PaneSubtitle,
  PaneTitle,
  createPaneAccessAwareFallback,
  isNoAccessError,
} from '../components/pane';
import { PaneErrorBoundary } from '../components/pane/PaneErrorBoundary';
import { PaneFooter } from '../components/pane/PaneFooter';
import { MULTISITE_DASHBOARD_POLL_QUERY_OPTIONS } from '../multisiteDashboardDataRefresh';

const INTERNAL_QUOTES_FALLBACK =
  createPaneAccessAwareFallback('internal quotes');

const STATUS_DATE_LABEL_MAP: Record<string, string> = {
  due_date: 'Due Date',
  expired_date: 'Expires',
  ordered_date: 'Ordered On',
};

const PAGE_SIZE = 20;

type InternalSubquoteCardProps = {
  subquote: InternalSubquote;
  availableGroups: CollectionUserGroupData[];
};

const InternalSubquoteCard = ({
  subquote,
  availableGroups,
}: InternalSubquoteCardProps) => {
  const { initChangeSite } = useUserGroupCollectionContext();
  const hasAccess = availableGroups.some(
    (g) => g.id === Number(subquote.userGroup.id),
  );

  const excessQuoteItemCount =
    subquote.lineItemsCount - subquote.lineItems.length;

  const handleQuoteClick = useCallback(() => {
    if (hasAccess) {
      initChangeSite({
        nextUrl: `/quotes/edit/${subquote.quoteNumberWithRevision}`,
        userGroupId: Number(subquote.userGroup.id),
      });
    }
  }, [hasAccess, initChangeSite, subquote]);

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="separator.border"
      borderRadius="md"
      overflow="hidden"
    >
      {/* Card header */}
      <HStack
        p={1}
        borderBottom="1px solid"
        borderBottomColor="separator.border"
        justify="space-between"
      >
        <HStack gap={0.5}>
          {hasAccess ? (
            <Button variant="link" size="md" onClick={handleQuoteClick}>
              Quote #{subquote.quoteNumberWithRevision}
            </Button>
          ) : (
            <Text fontSize="sm" noOfLines={1}>
              Quote #{subquote.quoteNumberWithRevision}
            </Text>
          )}
          <QuoteStatusBadge size="sm" quoteStatus={subquote.status} />
        </HStack>
        <UserGroupBadge
          name={subquote.userGroup.name}
          icon={subquote.userGroup.icon}
          colorPalette={subquote.userGroup.color}
          size="sm"
          minWidth="0"
        />
      </HStack>
      <VStack
        divider={<Divider />}
        align="stretch"
        spacing={0}
        backgroundColor={
          subquote.lineItems.length === 0 ? 'page.plateLight' : undefined
        }
      >
        {subquote.lineItems.length === 0 ? (
          <Empty
            size="sm"
            flexDirection="row"
            margin="auto"
            gap={0.5}
            px={2}
            py={1.25}
          >
            <EmptyDrawerIcon boxSize={3} />
            <EmptyDescription fontWeight="medium">
              No Line items
            </EmptyDescription>
          </Empty>
        ) : (
          subquote.lineItems.map((lineItem) => (
            <HStack key={lineItem.position} gap={1} px={1} py={0.5}>
              <Box
                textAlign="right"
                width={2}
                fontSize="sm"
                lineHeight="sm"
                fontWeight="medium"
                noOfLines={1}
              >
                {lineItem.position}
              </Box>
              <Part
                variant="borderless"
                size="md"
                flex={1}
                number={lineItem.part?.partNumber}
                rev={lineItem.part?.revision ?? undefined}
                filename={lineItem.part?.filename}
                isItar={lineItem.part?.isExportControlled}
              >
                <PartThumbnailContainer>
                  <PartThumbnail
                    partFileUuid={lineItem.part?.primaryFileUuid}
                    filename={lineItem.part?.filename}
                    fileType={lineItem.part?.fileType}
                    isAssembly={lineItem.part?.isAssembly}
                    containsVirus={lineItem.part?.containsVirus ?? false}
                    hasFailedChildren={
                      lineItem.part?.hasFailedChildren ?? false
                    }
                    processingFailed={lineItem.part?.processingFailed ?? false}
                  />
                </PartThumbnailContainer>
                <PartLabel flex={1} />
                <HStack gap={0.5} align="center">
                  <Avatar
                    size="sm"
                    name={[
                      lineItem.estimator?.firstName,
                      lineItem.estimator?.lastName,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    backgroundColor={
                      lineItem.estimator?.avatarColor ?? undefined
                    }
                  />
                  <QuoteItemStatusBadge status={lineItem.workflowStatus} />
                </HStack>
              </Part>
            </HStack>
          ))
        )}
        {excessQuoteItemCount > 0 && (
          <Center bg="page.plateLight" py={0.75} px={1}>
            {hasAccess ? (
              <Button
                icon={<ChevronRightIcon />}
                variant="link"
                size="sm"
                onClick={handleQuoteClick}
              >
                {pluralize('more line item', excessQuoteItemCount)} in quote
              </Button>
            ) : (
              <Text>
                {pluralize('more line item', excessQuoteItemCount)} in quote
              </Text>
            )}
          </Center>
        )}
      </VStack>
    </Box>
  );
};

type InternalQuoteCardProps = {
  quote: InternalQuote;
  availableGroups: CollectionUserGroupData[];
};

const InternalQuoteCard = ({
  quote,
  availableGroups,
}: InternalQuoteCardProps) => {
  const { initChangeSite } = useUserGroupCollectionContext();
  const { toDateString } = useDateFormat();

  const handleQuoteClick = () => {
    initChangeSite({
      nextUrl: `/quotes/edit/${quote.quoteNumberWithRevision}`,
      userGroupId: Number(quote.userGroup.id),
    });
  };

  const statusDateLabel = quote.statusDateLabel
    ? STATUS_DATE_LABEL_MAP[quote.statusDateLabel]
    : null;

  const statusDateValue = quote.statusDate
    ? toDateString(quote.statusDate)
    : '--';

  const workflowStatusLabel =
    quote.workflowStatus === WORKFLOW_OR_QUOTE_ITEM_STATUS.ON_HOLD
      ? `${pluralize('item', quote.onHoldCount)} on hold`
      : (WORKFLOW_OR_QUOTE_ITEM_STATUS_LABEL as Record<string, string>)[
          quote.workflowStatus
        ] ?? quote.workflowStatus;

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="separator.border"
      borderRadius="md"
    >
      {/* Card header */}
      <HStack
        p={1}
        borderBottom="1px solid"
        borderBottomColor="separator.border"
        justify="space-between"
      >
        <HStack gap={0.5}>
          <Button variant="link" size="md" onClick={handleQuoteClick}>
            Quote #{quote.quoteNumberWithRevision}
          </Button>
          <QuoteStatusBadge size="sm" quoteStatus={quote.status} />
        </HStack>
        <UserGroupBadge
          name={quote.userGroup.name}
          icon={quote.userGroup.icon}
          colorPalette={quote.userGroup.color}
          size="sm"
          minWidth="0"
        />
      </HStack>

      {/* Card body */}
      <HStack gap={1.5} pt={1} px={1.5} pb={0.5} alignItems="flex-start">
        <Box flexBasis="30%">
          <FormLabel as="span" m={0} height="auto">
            RFQ #
          </FormLabel>
          <Text fontSize="sm" noOfLines={2}>
            {quote.rfqNumber ?? '--'}
          </Text>
        </Box>
        <Box flexBasis="45%">
          <FormLabel as="span" m={0} height="auto">
            Customer
          </FormLabel>
          <Text fontSize="sm" noOfLines={2}>
            {quote.customerDisplayName ?? '--'}
          </Text>
        </Box>
        <Box flexBasis="25%">
          <FormLabel as="span" m={0} height="auto">
            {statusDateLabel ?? '--'}
          </FormLabel>
          <Text fontSize="sm" noOfLines={2}>
            {statusDateValue}
          </Text>
        </Box>
      </HStack>

      {/* Card footer */}
      <Accordion variant="compact">
        <AccordionItem borderBottomRadius="md">
          <AccordionButton
            justifyContent="space-between"
            borderRadius="inherit"
            _expanded={{
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            <Box as="span" flex="1" textAlign="left">
              {pluralize('internal quote', quote.subquotesCount)}
            </Box>
            <HStack gap={0.5}>
              {statusesToIcon[quote.workflowStatus]}
              <Text fontSize="sm">{workflowStatusLabel}</Text>
            </HStack>
          </AccordionButton>
          <AccordionPanel
            as={VStack}
            bg="page.plateMedium"
            padding={1}
            spacing={1}
            align="stretch"
          >
            {quote.subquotes.map((subquote) => (
              <InternalSubquoteCard
                key={subquote.quoteNumberWithRevision}
                subquote={subquote}
                availableGroups={availableGroups}
              />
            ))}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

type InternalQuotesTableProps = {
  activeGroupIds: number[];
};

const InternalQuotesTable = ({ activeGroupIds }: InternalQuotesTableProps) => {
  const { availableGroups } = useUserGroupCollectionContext();
  const [currentPage, setCurrentPage] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { overflowBox, updateOverflowBox } = useOverflowBox(scrollRef);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateOverflowBox, { passive: true });
    window.addEventListener('resize', updateOverflowBox);
    return () => {
      el.removeEventListener('scroll', updateOverflowBox);
      window.removeEventListener('resize', updateOverflowBox);
    };
  }, [updateOverflowBox]);

  const apiQuery = useMemo(
    () => ({
      ...(activeGroupIds.length > 0 ? { groups: activeGroupIds } : {}),
      page: currentPage,
      pageSize: PAGE_SIZE,
    }),
    [activeGroupIds, currentPage],
  );

  const {
    data,
    error: internalQuotesError,
    isFetching,
  } = useGetInternalQuotesOverview({
    query: apiQuery,
    queryOptions: {
      // The 'no_access' 403 is handled inline (below) instead of thrown to
      // the pane's error boundary; any other error still throws as before.
      useErrorBoundary: (error) => !isNoAccessError(error),
      keepPreviousData: true,
      ...MULTISITE_DASHBOARD_POLL_QUERY_OPTIONS,
    },
  });

  const currentPageQuotes = data?.results ?? [];
  const totalItems = data?.count ?? 0;

  const activeGroupNames = useMemo(
    () =>
      availableGroups
        .filter((g) => activeGroupIds.includes(g.id))
        .map((g) => g.businessName),
    [availableGroups, activeGroupIds],
  );

  if (isNoAccessError(internalQuotesError)) {
    return (
      <PaneBody bg="page.background">
        <PaneNoAccessState resourceLabel="internal quotes" />
      </PaneBody>
    );
  }

  return (
    <>
      <PaneBody bg="page.background" position="relative" minHeight="0">
        {isFetching && (
          <Center
            as="span"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex="overlay"
            bg="page.background"
            opacity={0.5}
          >
            <Spinner size="xl" />
          </Center>
        )}

        {currentPageQuotes.length === 0 && !isFetching ? (
          <Empty size="md" margin="auto">
            <EmptyDrawerIcon />
            <EmptyHeadline>No internal quotes found</EmptyHeadline>
            <EmptyDescription>
              No Draft, Outstanding, or Accepted quotes in{' '}
              {formatConjunctionWithTerm(activeGroupNames, 'or', Infinity)} have
              internal quotes
            </EmptyDescription>
          </Empty>
        ) : (
          <>
            <VStack
              ref={scrollRef}
              padding={1}
              gap={1}
              align="stretch"
              width="100%"
              overflowY="auto"
              minHeight={0}
            >
              {currentPageQuotes.map((quote) => (
                <InternalQuoteCard
                  key={quote.id}
                  quote={quote}
                  availableGroups={availableGroups}
                />
              ))}
            </VStack>
            <OverscrollShadowContainer
              overflowBox={overflowBox}
              stickyContentBox={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <OverscrollShadow
                gridArea="inside"
                boxShadow={`${SHADOW_FROM_DIRECTION.TOP}, ${SHADOW_FROM_DIRECTION.BOTTOM}`}
              />
            </OverscrollShadowContainer>
          </>
        )}
      </PaneBody>
      {totalItems > PAGE_SIZE && (
        <PaneFooter justifyContent="space-between">
          <Text fontSize="md">
            {(currentPage - 1) * PAGE_SIZE + 1} -{' '}
            {Math.min(currentPage * PAGE_SIZE, totalItems)} of{' '}
            {pluralize('quote', totalItems)}
          </Text>
          <Pagination
            totalItems={totalItems}
            itemsPerPage={PAGE_SIZE}
            currentPage={currentPage}
            onChangePage={setCurrentPage}
          />
        </PaneFooter>
      )}
    </>
  );
};

export type InternalQuotesPaneProps = {
  activeGroupIds: number[];
};

export const InternalQuotesPane = ({
  activeGroupIds,
}: InternalQuotesPaneProps) => {
  const websocketOptions = useMemo(() => {
    return {
      topic: activeGroupIds.map((id) => `${id}.quotes.created`),
    };
  }, [activeGroupIds]);

  return (
    <Pane
      refetchQueryKey={getInternalQuotesOverviewQueryKeyPrefix()}
      websocketOptions={websocketOptions}
    >
      <PaneHeader>
        <PaneTitle>Internal Quotes</PaneTitle>
        <PaneSubtitle>Created in the last 90 days</PaneSubtitle>
      </PaneHeader>
      <PaneErrorBoundary fallback={INTERNAL_QUOTES_FALLBACK}>
        <InternalQuotesTable activeGroupIds={activeGroupIds} />
      </PaneErrorBoundary>
    </Pane>
  );
};
